import {
  filter,
  filterSheetButton,
  filterSheetFooterTemplate,
  filterSheetTemplate,
  filterTitles,
  localNavigation,
  menuBarButtonForm,
  selectedNavigationTitle,
} from "./Selectors";
import { setActiveConfiguration, setOpenFilterIds } from "../lib/filter";
import { clearEphemeralFirstLevelValues, commitEphemeralOrder, savePendingEphemeralOrder } from "../lib/facet";
import { FilterTitle } from "../filter/FilterTitle";
import { Filter } from "../filter/Filter";
import { FacetValueTag } from "../filter/FacetValueTag";
import { isSelected, SvelteFacetValue } from "../filter/SvelteFacetValue";
import { RefinementBar } from "../refinementBar/RefinementBar";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { forEachElement, moveAllChildren } from "../util/Utils";
import { FilterSection } from "../filter/FilterSection";
import { FilterTitles } from "../filter/FilterTitles";
import { type OcSheetV1Events, sheetV1 } from "@otto-ec/otto-components/sheet";
import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { eventQBus } from "../types/EventQBus";
import { registerClickTracker, sanFoldout } from "./FilterSheetTracking";
import { readLegacyLabel } from "../tracking/FeatureTracking";
import { readSanPopularFacetValues, updateSanPopularFacetValues } from "../filter/SveltePopularFacetValues";
import PartialLock from "../util/PartialLock";
import { hasChanges } from "../filter/FilterFormActions";
import { readSanTopQuickFacet, updateSanTopQuickFacet } from "../nextBestAction/NextBestActionComponent";
import { OcFilterAccordion } from "../filter/OcFilterAccordion";
import { SortingInput } from "../sorting/SortingInput";
import { isActive, isNotActive } from "../toggle/Toggle";
import { exitSecondLevel, isSecondLevelActive } from "./AllLevelSheet";
import { events } from "../events";
import { blockSheetContentClick } from "../util/blockSheetContentClick";

const FILTER_SHEET_ID = "heureka-filtersheet";
const FILTER_SHEET_CONTENT_CLASS = "heureka_filterSheet__content";
const FILTER_SHEET_ACTIONS_CLASS = "heureka_filterSheet__actions";
const FILTER_SHEET_CONTENT_SELECTOR = `.${FILTER_SHEET_CONTENT_CLASS}`;
const FILTER_SHEET_ACTIONS_SELECTOR = `.${FILTER_SHEET_ACTIONS_CLASS}`;
const FILTER_SHEET_LOADING_CLASS = `${FILTER_SHEET_CONTENT_CLASS}--loading`;

export const FIRST_LEVEL_CHANGE_TRACKING_LABELS: Partial<TrackingLabels> = {
  san_FacetLocation: "first_level",
};
export const SIDEBAR_CHANGE_TRACKING_LABELS: Partial<TrackingLabels> = {
  san_FacetLocation: "sidebar",
};
export const SECOND_LEVEL_CHANGE_TRACKING_LABELS: Partial<TrackingLabels> = {
  san_FacetLocation: "second_level",
};
export const NBF_SHEET_CHANGE_TRACKING_LABELS: Partial<TrackingLabels> = {
  san_FacetLocation: "nbf_sheet",
};

export default class OcFilterSheet {
  static readonly factory = HeurekaElementFactory.byId(FILTER_SHEET_ID, OcFilterSheet);

  /*               */

  constructor(
    readonly sheet?: sheetV1,
    private readonly sheetContent: HTMLElement | null | undefined = sheet?.querySelector(FILTER_SHEET_CONTENT_SELECTOR),
    private readonly sheetActions: HTMLElement | null | undefined = sheet?.querySelector(FILTER_SHEET_ACTIONS_SELECTOR),
  ) {}

  public static pick() {
    return OcFilterSheet.factory.pick();
  }

  public static declareIfOpen(): OcFilterSheet | undefined {
    const sheet = OcFilterSheet.factory.byId(FILTER_SHEET_ID);
    return sheet?.sheet?.open ? sheet : undefined;
  }

  public static declare(): OcFilterSheet | undefined {
    const sheet = OcFilterSheet.factory.byId(FILTER_SHEET_ID);
    return sheet?.sheet ? sheet : undefined;
  }

  public static savePopularValuesOrder(event?: Event) {
    if (event) {
      const target = event.target;
      if (!(target instanceof HTMLElement && target.classList.contains("hc_facetValueInput"))) return;
      const scrollBox = target.closest(".hc_facet__scrollBox");
      if (!scrollBox) return;
      const filterId = target.closest("form[id]")?.id?.replace("find_filter_", "");
      if (!filterId) return;

      const values: string[] = [];
      scrollBox.querySelectorAll<HTMLInputElement>(".hc_facetValueInput").forEach((input) => {
        if (input.value) values.push(input.value);
      });
      savePendingEphemeralOrder(filterId, values);
    }
  }

  public static commitPopularValuesOrder() {
    commitEphemeralOrder();
  }

  public static dropPopularValuesOrder() {
    clearEphemeralFirstLevelValues();
  }

  /*       */

  setSheet() {
    const sheetFilterTitles = filterTitles();
    if (sheetFilterTitles) {
      this.sheetContent?.appendChild(sheetFilterTitles);
    }
    return this;
  }

  initializeSheet() {
    const content = this.sheetContent;
    if (content) {
      setOpenFilterIds([]);
      setTimeout(() => {
        setOpenFilterIds(OcFilterAccordion.factory.all().map((a) => a.filterId));
      }, 0);
      setActiveConfiguration("sheetFirstLevel");
      updateSanPopularFacetValues(content, readSanPopularFacetValues());
      updateSanTopQuickFacet(content, readSanTopQuickFacet());

      OcFilterSheet.moveDesktopInlinedFiltersToSheet();
      if (isNotActive("HEUREKA_2398_OPTIMIZE_FILTER_SHEET_PERFORMANCE")) {
        moveAllChildren(filterSheetFooterTemplate(), this.sheetActions, true);
      }

      commitEphemeralOrder();
      const titles = filterTitles();
      if (titles) {
        titles.removeEventListener("change", OcFilterSheet.savePopularValuesOrder, { capture: true });
        titles.addEventListener("change", OcFilterSheet.savePopularValuesOrder, { capture: true });
      }

      /*                                                                 */
      /*                                                                            */
      menuBarButtonForm()?.addEventListener(
        "click",
        () => {
          FacetValueTag.factory.forEach((tag) => {
            tag.updateTrackingLabelsOnReferenced("deleted", "x_all_local", "first_level");
          });
          SvelteFacetValue.factory
            .all(RefinementBar.facetValueTagsElement())
            .filter(isSelected)
            .forEach((tag) => {
              tag.updateTrackingLabelsOnReferenced("deleted", "x_all_local", "first_level");
            });
        },
        { capture: true },
      );
    }
    return this;
  }

  /*                       */

  deactivate() {
    /*                                                                                                             */
    /*                                                                 */
    if (isActive("HEUREKA_2398_ALL_LEVEL_SHEET") && isSecondLevelActive()) {
      exitSecondLevel();
    }

    const source = this.sheetContent;
    if (source) {
      OcFilterSheet.fireDelayedActionsNowOrNever();
      if (isNotActive("HEUREKA_2398_OPTIMIZE_FILTER_SHEET_PERFORMANCE")) {
        OcFilterSheet.moveInlinedFiltersFrom(source, true, SIDEBAR_CHANGE_TRACKING_LABELS);
        moveAllChildren(source, filterSheetTemplate(), true);
      }
      clearEphemeralFirstLevelValues();
    }

    if (isNotActive("HEUREKA_2398_OPTIMIZE_FILTER_SHEET_PERFORMANCE")) {
      if (this.sheetActions) {
        moveAllChildren(this.sheetActions, filterSheetFooterTemplate(), true);
      }
    }
  }

  public lockContent() {
    if (this.sheet) {
      this.sheetContent?.classList.add(FILTER_SHEET_LOADING_CLASS);
      if (isActive("HEUREKA_2437_FIX_LOCK_ALL")) {
        this.sheetContent?.addEventListener("click", blockSheetContentClick, true);
      }
    }
    return this;
  }

  public unlockContent() {
    this.sheetContent?.classList.remove(FILTER_SHEET_LOADING_CLASS);
    if (isActive("HEUREKA_2437_FIX_LOCK_ALL")) {
      this.sheetContent?.removeEventListener("click", blockSheetContentClick, true);
    }
    return this;
  }

  /*                          */
  public updateContent() {
    if (this.sheetContent) {
      this.sheetContent.innerHTML = "";
    }
    this.setSheet().initializeSheet();
    this.sheetActions?.replaceChildren();
    moveAllChildren(filterSheetFooterTemplate(), this.sheetActions, true);
    return this;
  }

  public removeContent() {
    if (this.sheetContent) {
      this.sheetContent.innerHTML = "";
    }
    this.sheetActions?.replaceChildren();
    return this;
  }

  public close() {
    if (this.sheet) {
      this.deactivate();
      this.sheet.close();
    }
    return this;
  }

  public open() {
    if (this.sheet) {
      this.sheet.open = true;
    }
    return this;
  }

  public setScrollOffset(offset?: number) {
    if (typeof offset === "number") {
      const content: HTMLElement = this.sheet as HTMLElement;
      if (content) {
        content.scrollTop = offset;
      }
    }
    return this;
  }

  /*       */
  static moveDesktopInlinedFiltersToSheet() {
    FilterTitle.factory.forEach((filterTitle) => {
      Filter.updateChangeTrackingLabels(filterTitle.filterId, FIRST_LEVEL_CHANGE_TRACKING_LABELS);
      filterTitle.attachFilter();
    });
  }

  public static scrollToRecentFilter(recentFilterId: string) {
    if (recentFilterId) {
      const filterSheetFooterRect = OcFilterSheet.pick()
        .sheet?.shadowRoot?.querySelector(".sheet__footer")
        .getBoundingClientRect();
      const filterTitle = Filter.filterId(recentFilterId)?.filterTitle?.elem;
      const filterTitleRect = filterTitle.getBoundingClientRect();

      if (OcFilterSheet.isHiddenByFooter(filterTitleRect, filterSheetFooterRect) && filterTitle.scrollIntoView) {
        filterTitle.scrollIntoView(true);
      }
    }
  }

  static isHiddenByFooter(titleRect: DOMRect, footerRect: DOMRect): boolean {
    return titleRect && footerRect && titleRect.bottom > footerRect.top;
  }

  static moveInlinedFiltersFrom(
    content: HTMLElement,
    updateChangeTracking: boolean,
    trackingLabel: Partial<TrackingLabels>,
  ) {
    FilterTitle.factory.forEach((filterTitle) => {
      if (updateChangeTracking) {
        Filter.updateChangeTrackingLabels(filterTitle.filterId, trackingLabel);
      }
      filterTitle.detachFilter();
    });
  }

  public static addLocalNavigationTitleValue() {
    const title = selectedNavigationTitle(localNavigation());
    const titles = filterTitles();
    if (titles && title) {
      const { overlayEntryTitle = "" } = title.dataset;
      forEachElement(".js_selectedNavigationFilterSheet", (elem) => (elem.innerText = overlayEntryTitle), titles);
    }
  }

  public static addInitialLoadError() {
    const errorMessage = FilterSection.template()?.errorMessage;
    if (errorMessage) {
      FilterTitles.template()?.elem.append(errorMessage.cloneNode(true));
    }
  }

  public addListeners() {
    if (this.sheet) {
      /*         */
      this.sheet?.addEventListener("oc-sheet-before-open", (event: OcSheetV1Events["oc-sheet-before-open"]) => {
        /*                                                                                       */
        /*                                                                                                                            */
        /*                                                                                                             */
        const hasActiveSheet = !!document.querySelector("oc-sheet-v1[open]");
        if (event.detail.action === "user-agent" && !hasActiveSheet) {
          event.preventDefault();
          return;
        }
        if (isActive("HEUREKA_2437_FIX_LOCK_ALL")) {
          /*                                    */
          this.unlockContent();
        }
        if (isActive("HEUREKA_2398_OPTIMIZE_FILTER_SHEET_PERFORMANCE")) {
          setActiveConfiguration("sheetFirstLevel");
        } else {
          this.setSheet().initializeSheet();
        }
        SortingInput.resyncToCurrentUrl(this.sheetContent ?? undefined);
      });

      this.sheet?.addEventListener("oc-sheet-after-open", (event: OcSheetV1Events["oc-sheet-after-open"]) => {
        /*                                                                                             */
        /*                                                           */
        /*                                                              */
        if (event.detail.reason === "content-switch") {
          return;
        }
        eventQBus.emit("ftfind.filterSheet.opened", { filterId: undefined });
      });

      /*          */
      this.sheet?.addEventListener("oc-sheet-before-close", (event: OcSheetV1Events["oc-sheet-close"]) => {
        /*                                                     */
        /*                                                                                             */
        if (event.detail.closeType === "closedWithUserAgent") {
          event.preventDefault();
          return;
        }

        if (this.sheet?.classList.contains(FILTER_SHEET_LOADING_CLASS)) {
          event.preventDefault();
          return;
        }

        /*                                                                                     */
        if (event.detail.closeType !== "closedProgrammatically") {
          this.deactivate();
        }
      });
      this.sheet?.addEventListener("oc-sheet-after-close", (event) => {
        const { tsLabels = "{}" } = this.sheetContent?.dataset || {};
        const san_FilterWhatIf = readLegacyLabel(filterSheetButton(), "san_FilterWhatIf");
        if (event.detail.closeType !== "closedProgrammatically") {
          eventQBus.emit("ftfind.filterSheet.closed", {
            filterId: undefined,
            tracking: {
              san_Foldout: sanFoldout(event.detail.closeType),
              san_FilterWhatIf,
              ...JSON.parse(tsLabels),
            },
          });
        }
      });

      registerClickTracker(this.sheet, {
        capture: true,
        passive: true,
      });
    }
  }

  public static initPartialLocking() {
    const titles = filterTitles();
    if (titles) {
      PartialLock.create(
        titles,
        ".find_filterTitles__item, .heureka_filterTitle",
        (lockOwner) => {
          const filterId = lockOwner.dataset.filterId;
          const filterForm = filterId && filter(filterId);
          if (filterForm) {
            filterForm.delayedAction?.nowOrNever();
            return hasChanges(filterForm);
          }
          return false;
        },
        isActive("HEUREKA_2407_MIGRATE_TO_REPTILE_NEXUS")
          ? {
              unlockTopics: ["heureka.filterSection.loadAborted"],
              unlockOn: [events.onDresonRuleResolveAborted, events.onDresonRuleResolved],
            }
          : {
              unlockTopics: [
                "heureka.filterSection.loadAborted",
                "ftfind.dresonRule.resolveAborted",
                "ftfind.dresonRule.resolved",
              ],
            },
      );
    }
  }

  public static addErrorsOnFilterTitleValues(filterIds?: string[]) {
    const titles = filterTitles();
    if (titles) {
      const invalidIds = filterIds || OcFilterAccordion.invalidFilterIds();

      invalidIds.forEach((filterId) =>
        forEachElement(
          `.find_filterTitles__item[data-filter-id="${filterId}"]`,
          (item) => item.classList.add("find_filterTitles__item--invalid"),
          titles,
        ),
      );
    }
  }

  public static fireDelayedActionsNowOrNever() {
    const titles = filterTitles();
    if (titles) {
      FilterTitle.factory.forEach((filterTitle) => filterTitle.filter?.form?.delayedAction?.nowOrNever());
    }
  }
}
